
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { Store, SubscriptionTier } from '../types';
import { billingService, SUBSCRIPTION_PLANS } from '../services/billingService';
import { 
  CreditCard, 
  CheckCircle2, 
  Zap, 
  ShieldCheck, 
  Package, 
  ArrowRight, 
  Loader2,
  Calendar,
  AlertCircle,
  Clock,
  Check
} from 'lucide-react';

interface AdminBillingProps {
  storeId: string;
}

const AdminBilling: React.FC<AdminBillingProps> = ({ storeId }) => {
  const [store, setStore] = useState<Store>(storage.getStoreConfig(storeId));
  const [isUpgrading, setIsUpgrading] = useState<SubscriptionTier | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [productsCount, setProductsCount] = useState(0);

  useEffect(() => {
    setProductsCount(storage.getProducts().filter(p => p.storeId === storeId).length);
    billingService.processBillingCycle(storeId);
    setStore(storage.getStoreConfig(storeId));
  }, [storeId]);

  const currentTier = store.subscription?.tier || 'STARTER';
  const currentPlan = SUBSCRIPTION_PLANS[currentTier];
  const isOverdue = store.subscription?.status === 'OVERDUE';

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setIsUpgrading(tier);
    await billingService.upgradePlan(storeId, tier);
    setStore(storage.getStoreConfig(storeId));
    setIsUpgrading(null);
  };

  const handlePayFee = async () => {
    setIsPaying(true);
    await billingService.payMonthlyFee(storeId);
    setStore(storage.getStoreConfig(storeId));
    setIsPaying(false);
  };

  const usagePercent = Math.min(100, (productsCount / currentPlan.productLimit) * 100);

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Billing & SaaS Access</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Global Subscription Engine</p>
        </div>
      </div>

      {isOverdue && (
        <div className="bg-red-50 border-2 border-red-100 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6 animate-pulse">
           <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-red-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                 <AlertCircle className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                 <h3 className="text-xl font-black text-red-900 uppercase">Payment Overdue</h3>
                 <p className="text-sm text-red-700 font-medium">Bhai, aapka billing period expire ho chuka hai. Product additions restricted hain.</p>
              </div>
           </div>
           <button 
             onClick={handlePayFee}
             disabled={isPaying}
             className="bg-red-600 text-white px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-red-700 transition-all flex items-center gap-2"
           >
              {isPaying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> Pay ${currentPlan.price} Now</>}
           </button>
        </div>
      )}

      {/* Current Plan Status */}
      <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl flex flex-col md:flex-row gap-10 items-center justify-between">
         <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center text-white shadow-2xl" style={{ backgroundColor: currentPlan.color }}>
               <Zap className="h-10 w-10" />
            </div>
            <div>
               <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Current Status</p>
               <h3 className="text-3xl font-black text-gray-900">{currentPlan.name} Plan</h3>
               <div className="flex items-center gap-4 mt-1">
                  <p className={`text-xs font-bold flex items-center gap-1 ${isOverdue ? 'text-red-500' : 'text-emerald-500'}`}>
                    <CheckCircle2 className="h-3 w-3" /> {isOverdue ? 'Action Required' : 'Status: Active'}
                  </p>
                  <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                  <p className="text-gray-400 text-xs font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Next Bill: {new Date(store.subscription?.expiryDate || Date.now()).toLocaleDateString()}
                  </p>
               </div>
            </div>
         </div>

         <div className="w-full md:w-80 space-y-3">
            <div className="flex justify-between items-end">
               <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Slot Usage</p>
               <p className="text-sm font-black text-gray-900">{productsCount} / {currentPlan.productLimit === 10000 ? '∞' : currentPlan.productLimit}</p>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
               <div 
                 className="h-full transition-all duration-1000" 
                 style={{ width: `${usagePercent}%`, backgroundColor: currentPlan.color }}
               ></div>
            </div>
         </div>
      </div>

      {/* Pricing Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
         {(Object.keys(SUBSCRIPTION_PLANS) as SubscriptionTier[]).map((tier) => {
            const plan = SUBSCRIPTION_PLANS[tier];
            const isCurrent = currentTier === tier;

            return (
              <div 
                key={tier} 
                className={`bg-white rounded-[40px] p-10 border-2 transition-all relative overflow-hidden flex flex-col ${
                  isCurrent ? 'border-gray-900 shadow-2xl' : 'border-gray-100 hover:border-gray-200 shadow-xl'
                }`}
              >
                 {isCurrent && (
                   <div className="absolute top-0 right-0 bg-gray-900 text-white px-6 py-2 rounded-bl-3xl text-[10px] font-black uppercase tracking-widest">
                      Primary
                   </div>
                 )}
                 
                 <div className="space-y-2 mb-8">
                    <h4 className="text-xl font-black text-gray-900">{plan.name}</h4>
                    <div className="flex items-baseline gap-1">
                       <span className="text-4xl font-black text-gray-900">${plan.price}</span>
                       <span className="text-gray-400 font-bold">/month</span>
                    </div>
                 </div>

                 <ul className="space-y-4 mb-10 flex-grow">
                    {plan.features.map((feat, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm font-bold text-gray-600">
                         <div className="bg-gray-50 p-1.5 rounded-lg text-gray-400">
                            <CheckCircle2 className="h-4 w-4" />
                         </div>
                         {feat}
                      </li>
                    ))}
                 </ul>

                 <button 
                   onClick={() => handleUpgrade(tier)}
                   disabled={isCurrent || isUpgrading !== null}
                   className={`w-full py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                     isCurrent 
                     ? 'bg-gray-100 text-gray-400 cursor-default' 
                     : 'bg-gray-900 text-white hover:bg-black shadow-xl'
                   }`}
                 >
                    {isUpgrading === tier ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isCurrent ? (
                      'Manage Current'
                    ) : (
                      <>Upgrade <ArrowRight className="h-4 w-4" /></>
                    )}
                 </button>
              </div>
            );
         })}
      </div>

      {/* Cycle History */}
      <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
         <div className="p-10 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-2xl font-black text-gray-900 tracking-tight">Ledger & History</h3>
            <button className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center gap-2">
               Download All <Calendar className="h-4 w-4" />
            </button>
         </div>
         <div className="divide-y divide-gray-50">
            {store.subscription?.lastBilledAt && (
              <div className="p-8 flex items-center justify-between hover:bg-gray-50 transition-all">
                 <div className="flex items-center gap-6">
                    <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl"><Check className="h-6 w-6" /></div>
                    <div>
                       <p className="font-black text-gray-900">Monthly SaaS Fee - {currentPlan.name}</p>
                       <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                         Cycle Date: {new Date(store.subscription.lastBilledAt).toLocaleDateString()} • Ref #LYNQ-BILL-{Date.now().toString().slice(-4)}
                       </p>
                    </div>
                 </div>
                 <div className="text-right">
                    <p className="text-xl font-black text-gray-900">${currentPlan.price}</p>
                    <span className="text-[8px] font-black uppercase text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded">Success</span>
                 </div>
              </div>
            )}
         </div>
      </div>
    </div>
  );
};

export default AdminBilling;
